import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

import {
  MAX_COMPARED_LAPS,
  TraceWorkspace,
  useChannelStack,
} from "../components/TraceWorkspace";
import { ApiError, api, formatLapRef, parseLapRefs, type LapRef } from "../lib/api";
import { formatDayTime, formatLapTime, formatSignedSeconds } from "../lib/format";
import { alignTrace, type AlignedLap } from "../lib/trace";
import type { SameTrackLap, SameTrackSession, TraceResponse } from "../lib/types";

/** Where another lap is being picked from. In the URL, so a link can open on it. */
type PickSource = "mine" | "board";

function parsePick(raw: string | null): PickSource | null {
  return raw === "mine" || raw === "board" ? raw : null;
}

/**
 * Laps from several sessions, and usually several drivers, over one distance axis.
 *
 * The selection lives in the URL so a comparison can be sent to the person on the
 * other side of it.
 */
export function ComparePage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [params, setParams] = useSearchParams();

  const refs = useMemo(() => parseLapRefs(params.get("laps")), [params]);
  const pick = parsePick(params.get("pick"));
  const from = params.get("from");
  const { stack, toggleChannel, requestedChannels } = useChannelStack();

  const channels = useQuery({ queryKey: ["channels"], queryFn: api.channels });

  const key = refs.map(formatLapRef).join(",");
  const traces = useQuery({
    queryKey: ["compare", key, requestedChannels],
    queryFn: () => api.compareTraces(refs, requestedChannels),
    enabled: refs.length > 0,
    // Keeps the laps already drawn on screen while the new set loads, instead of
    // blanking the page — and closing the picker — every time a lap is added.
    placeholderData: keepPreviousData,
  });

  const aligned = useMemo(
    () => (traces.data ? alignTrace(traces.data) : null),
    [traces.data],
  );

  // Elapsed time at the last bucket both laps reached is the honest measure here:
  // these laps come from different files, and there is no shared timing sheet to
  // rank them by.
  const reference = useMemo<AlignedLap | null>(() => {
    if (!aligned || aligned.laps.length === 0) return null;
    return aligned.laps.reduce((best, lap) =>
      (finalTime(lap) ?? Infinity) < (finalTime(best) ?? Infinity) ? lap : best,
    );
  }, [aligned]);

  /** Rewrites one parameter and keeps the others, the picker's state included. */
  function update(name: string, value: string | null) {
    const next = new URLSearchParams(params);
    if (value) next.set(name, value);
    else next.delete(name);
    setParams(next, { replace: true });
  }

  function drop(target: string) {
    const remaining = refs.filter((ref) => formatLapRef(ref) !== target);
    update("laps", remaining.map(formatLapRef).join(","));
  }

  function add(ref: LapRef) {
    update("laps", [...refs, ref].map(formatLapRef).join(","));
  }

  const back = from ? `/sessions/${from}` : id ? `/teams/${id}` : "/leaderboard";

  return (
    <div>
      <Link to={back} className="text-sm text-zinc-500 hover:text-zinc-300">
        ← {t("compare.back")}
      </Link>
      <div className="mt-3 flex flex-wrap items-baseline gap-x-3">
        <h1 className="text-2xl font-semibold tracking-tight">{t("compare.title")}</h1>
        {traces.data?.track_name && (
          <p className="text-sm text-zinc-500">
            {traces.data.track_name}
            {traces.data.car_class && ` · ${traces.data.car_class}`}
          </p>
        )}
      </div>

      {refs.length === 0 ? (
        <p className="mt-6 text-sm text-zinc-400">{t("compare.empty")}</p>
      ) : traces.isPending ? (
        <p className="mt-6 text-sm text-zinc-500">{t("compare.loading")}</p>
      ) : traces.isError || !aligned ? (
        <p className="mt-6 text-sm text-red-400">{failureText(t, traces.error)}</p>
      ) : (
        <div
          className={[
            "mt-6 transition-opacity",
            traces.isPlaceholderData ? "opacity-60" : "",
          ].join(" ")}
        >
          <TraceWorkspace
            aligned={aligned}
            distanceStep={traces.data?.distance_step_m ?? 0}
            channels={channels.data ?? []}
            stack={stack}
            onToggleChannel={toggleChannel}
            reference={reference}
            selectionKey={key}
          >
            <div>
              <h2 className="text-sm font-medium uppercase tracking-wider text-zinc-500">
                {t("compare.laps")}
              </h2>
              <div className="mt-4 overflow-hidden rounded-xl border border-zinc-800">
                <table className="w-full text-sm">
                  <tbody className="divide-y divide-zinc-800">
                    {aligned.laps.map((lap) => {
                      const total = finalTime(lap);
                      const gap =
                        reference && total !== null && finalTime(reference) !== null
                          ? total - (finalTime(reference) as number)
                          : null;
                      return (
                        <tr key={lap.key}>
                          <td className="px-3 py-2">
                            <span className="flex items-center gap-2">
                              <span
                                className="h-2 w-2 shrink-0 rounded-full"
                                style={{ backgroundColor: lap.colour }}
                              />
                              <span className="text-zinc-200">{lap.label}</span>
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-zinc-400">
                            {gap === null || lap.key === reference?.key
                              ? t("compare.reference")
                              : formatSignedSeconds(gap)}
                          </td>
                          <td className="w-20 px-3 py-2 text-right">
                            {lap.sessionId && (
                              <Link
                                to={`/sessions/${lap.sessionId}`}
                                className="text-xs text-zinc-500 hover:text-sky-400"
                              >
                                {t("compare.session")}
                              </Link>
                            )}
                          </td>
                          <td className="w-8 px-2 py-2 text-right">
                            <button
                              type="button"
                              onClick={() => drop(lap.key)}
                              title={t("compare.remove")}
                              className="text-zinc-600 transition-colors hover:text-red-400"
                            >
                              ×
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {traces.data && (
                <AddLap
                  trace={traces.data}
                  present={refs}
                  fastest={reference ? finalTime(reference) : null}
                  source={pick}
                  onSource={(source) => update("pick", source)}
                  onAdd={add}
                />
              )}
            </div>
          </TraceWorkspace>
        </div>
      )}
    </div>
  );
}

interface AddLapProps {
  trace: TraceResponse;
  present: LapRef[];
  /** Elapsed time of the quickest lap on screen, in seconds, for the gap column. */
  fastest: number | null;
  source: PickSource | null;
  onSource: (source: PickSource | null) => void;
  onAdd: (ref: LapRef) => void;
}

/**
 * Puts another lap on the chart: one of your own, from any of your sessions on this
 * track, or another driver's best off the leaderboard.
 *
 * Both are offered up front rather than one behind the other, because they answer
 * different questions — "am I getting quicker here" and "where is the time" — and
 * the leaderboard alone could only ever offer a driver their single best lap.
 */
function AddLap({ trace, present, fastest, source, onSource, onAdd }: AddLapProps) {
  const { t } = useTranslation();
  const full = present.length >= MAX_COMPARED_LAPS;

  if (!source) {
    const closed =
      "w-full rounded-lg border border-dashed border-zinc-700 px-3 py-2 text-sm text-zinc-400 transition-colors hover:border-zinc-500 hover:text-zinc-200 disabled:opacity-40";

    return (
      <div className="mt-3 flex flex-col gap-2">
        {full ? (
          <button type="button" disabled className={closed}>
            {t("compare.add.limit", { count: MAX_COMPARED_LAPS })}
          </button>
        ) : (
          <>
            <button type="button" onClick={() => onSource("mine")} className={closed}>
              {t("compare.add.fromMine")}
            </button>
            <button
              type="button"
              onClick={() => onSource("board")}
              disabled={!trace.track_name}
              className={closed}
            >
              {t("compare.add.fromBoard")}
            </button>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="mt-3 rounded-xl border border-zinc-800">
      <div className="flex items-center gap-1 border-b border-zinc-800 px-2 py-1.5">
        {(["mine", "board"] as const).map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => onSource(option)}
            className={[
              "rounded-md px-2 py-1 text-xs font-medium transition-colors",
              source === option
                ? "bg-zinc-800 text-zinc-100"
                : "text-zinc-500 hover:text-zinc-300",
            ].join(" ")}
          >
            {t(`compare.add.tabs.${option}`)}
          </button>
        ))}
        <button
          type="button"
          onClick={() => onSource(null)}
          className="ml-auto px-1 text-xs text-zinc-500 hover:text-zinc-300"
        >
          {t("compare.add.close")}
        </button>
      </div>

      {full && (
        <p className="px-3 pt-2 text-xs text-amber-400/80">
          {t("compare.add.limit", { count: MAX_COMPARED_LAPS })}
        </p>
      )}

      {source === "mine" ? (
        <MyLaps
          anchor={present[0]?.sessionId}
          step={trace.distance_step_m}
          present={present}
          fastest={fastest}
          full={full}
          onAdd={onAdd}
        />
      ) : (
        <BoardLaps
          trace={trace}
          present={present}
          fastest={fastest}
          full={full}
          onAdd={onAdd}
        />
      )}
    </div>
  );
}

interface PickerListProps {
  present: LapRef[];
  fastest: number | null;
  full: boolean;
  onAdd: (ref: LapRef) => void;
}

/**
 * Every session of yours on this track, newest first.
 *
 * A driver comparing sessions almost always wants each one's best lap — "was I
 * quicker on Tuesday" — so that is one click, and every other lap of the session is
 * behind a toggle rather than spread across the panel by default.
 */
function MyLaps({
  anchor,
  step,
  ...list
}: PickerListProps & {
  /** Any session already on the chart; the track is read from it. */
  anchor: string | undefined;
  step: number;
}) {
  const { t } = useTranslation();
  const sessions = useQuery({
    queryKey: ["same-track", anchor],
    queryFn: () => api.sameTrackSessions(anchor as string),
    enabled: Boolean(anchor),
  });

  if (sessions.isPending) {
    return <p className="px-3 py-3 text-sm text-zinc-500">{t("compare.add.looking")}</p>;
  }
  if (sessions.isError) {
    return <p className="px-3 py-3 text-sm text-red-400">{t("compare.add.mineError")}</p>;
  }
  if (sessions.data.length === 0) {
    return <p className="px-3 py-3 text-sm text-zinc-500">{t("compare.add.mineNone")}</p>;
  }

  return (
    <ul className="divide-y divide-zinc-800">
      {sessions.data.map((session) => (
        <SessionLaps key={session.id} session={session} step={step} {...list} />
      ))}
    </ul>
  );
}

function SessionLaps({
  session,
  step,
  present,
  fastest,
  full,
  onAdd,
}: PickerListProps & { session: SameTrackSession; step: number }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const sameGrid =
    session.distance_step_m === null || Math.abs(session.distance_step_m - step) < 1e-9;
  const onChart = (lap: number) =>
    present.some((ref) => ref.sessionId === session.id && ref.lap === lap);
  const best = bestLap(session.laps);
  const shown = session.laps.filter((lap) => onChart(lap.lap_number)).length;
  const gap =
    best && fastest !== null ? (best.lap_time_ms as number) / 1000 - fastest : null;

  return (
    <li className={["px-3 py-2", sameGrid ? "" : "opacity-50"].join(" ")}>
      <div className="flex items-baseline gap-2">
        <span className="text-sm text-zinc-200">{formatDayTime(session.recorded_at)}</span>
        {session.session_kind && (
          <span className="text-xs text-zinc-500">{session.session_kind}</span>
        )}
        {shown > 0 && (
          <span className="text-[10px] uppercase tracking-wider text-sky-400">
            {t("compare.add.onChart", { count: shown })}
          </span>
        )}
        <span className="ml-auto flex shrink-0 items-baseline gap-1.5 tabular-nums">
          {best ? (
            <>
              <span className="text-sm text-zinc-300">{formatLapTime(best.lap_time_ms)}</span>
              <span className="text-xs text-zinc-500">L{best.lap_number}</span>
              {gap !== null && Math.abs(gap) >= 0.0005 && (
                <span className="text-xs text-zinc-500">{formatSignedSeconds(gap)}</span>
              )}
            </>
          ) : (
            <span className="text-xs text-zinc-600">{t("compare.add.noTimedLap")}</span>
          )}
        </span>
      </div>

      <div className="mt-1 flex items-center gap-2">
        <span className="min-w-0 truncate text-xs text-zinc-500">
          {[session.car_name, session.car_class].filter(Boolean).join(" · ")}
        </span>
        <span className="ml-auto flex shrink-0 items-center gap-2">
          {!sameGrid ? (
            <span
              className="text-[10px] uppercase tracking-wider text-zinc-600"
              title={t("compare.add.otherGridTitle")}
            >
              {t("compare.add.otherGrid")}
            </span>
          ) : (
            <>
              {session.laps.length > 0 && (
                <button
                  type="button"
                  onClick={() => setExpanded((open) => !open)}
                  className="text-xs text-zinc-500 hover:text-zinc-300"
                >
                  {expanded
                    ? t("compare.add.hideLaps")
                    : t("compare.add.allLaps", { count: session.laps.length })}{" "}
                  <span aria-hidden>{expanded ? "▴" : "▾"}</span>
                </button>
              )}
              {best &&
                (onChart(best.lap_number) ? (
                  <span className="text-xs text-sky-400">{t("compare.add.added")}</span>
                ) : (
                  <button
                    type="button"
                    disabled={full}
                    onClick={() => onAdd({ sessionId: session.id, lap: best.lap_number })}
                    className="rounded-md border border-zinc-700 px-2 py-0.5 text-xs text-zinc-300 transition-colors hover:border-sky-500 hover:text-sky-300 disabled:opacity-40"
                  >
                    {t("compare.add.addBest")}
                  </button>
                ))}
            </>
          )}
        </span>
      </div>

      {expanded && sameGrid && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {session.laps.map((lap) => {
            const here = onChart(lap.lap_number);
            return (
              <button
                key={lap.lap_number}
                type="button"
                disabled={full || here}
                onClick={() => onAdd({ sessionId: session.id, lap: lap.lap_number })}
                className={[
                  "rounded-md border px-1.5 py-0.5 text-xs tabular-nums transition-colors",
                  here
                    ? "border-sky-500/50 bg-sky-500/10 text-sky-300"
                    : "border-zinc-700 text-zinc-300 hover:border-sky-500 hover:text-sky-300 disabled:opacity-40",
                ].join(" ")}
              >
                <span className="text-zinc-500">L{lap.lap_number}</span>{" "}
                <span
                  className={
                    lap === best
                      ? "text-fuchsia-400"
                      : lap.is_valid === false
                        ? "text-amber-400"
                        : undefined
                  }
                >
                  {lap.lap_time_ms !== null
                    ? formatLapTime(lap.lap_time_ms)
                    : lap.is_out_lap
                      ? t("session.flags.out")
                      : lap.is_in_lap
                        ? t("session.flags.in")
                        : t("session.flags.invalid")}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </li>
  );
}

/**
 * Anyone's best lap of the same track and class, straight from the public
 * leaderboard. Laps on a different distance grid are shown but not offered, since
 * they cannot be laid over these ones and saying so beats silently hiding them.
 */
function BoardLaps({
  trace,
  present,
  fastest,
  full,
  onAdd,
}: PickerListProps & { trace: TraceResponse }) {
  const { t } = useTranslation();

  const candidates = useQuery({
    queryKey: ["leaderboard", trace.track_name, trace.car_class],
    queryFn: () =>
      api.publicLeaderboard({
        track: trace.track_name ?? undefined,
        carClass: trace.car_class ?? undefined,
      }),
    enabled: Boolean(trace.track_name),
  });

  const rows = useMemo(
    () =>
      (candidates.data ?? []).filter(
        (entry) =>
          !present.some(
            (ref) =>
              ref.sessionId === entry.session_id && ref.lap === entry.lap_number,
          ),
      ),
    [candidates.data, present],
  );

  if (candidates.isPending) {
    return <p className="px-3 py-3 text-sm text-zinc-500">{t("compare.add.looking")}</p>;
  }
  if (candidates.isError) {
    return <p className="px-3 py-3 text-sm text-red-400">{t("compare.add.error")}</p>;
  }
  if (rows.length === 0) {
    return <p className="px-3 py-3 text-sm text-zinc-500">{t("compare.add.none")}</p>;
  }

  return (
    <table className="w-full text-sm">
      <tbody className="divide-y divide-zinc-800">
        {rows.map((entry) => {
          const sameGrid =
            entry.distance_step_m === null ||
            Math.abs(entry.distance_step_m - trace.distance_step_m) < 1e-9;
          const gap = fastest === null ? null : entry.lap_time_ms / 1000 - fastest;

          return (
            <tr key={`${entry.session_id}:${entry.lap_number}`}>
              <td className="px-3 py-2">
                <span className="flex items-center gap-2">
                  {entry.avatar_url && (
                    <img
                      src={entry.avatar_url}
                      alt=""
                      className="size-5 rounded-full ring-1 ring-zinc-700"
                    />
                  )}
                  <span className="text-zinc-200">{entry.persona_name}</span>
                </span>
              </td>
              <td className="px-3 py-2 text-right tabular-nums text-zinc-300">
                {formatLapTime(entry.lap_time_ms)}
              </td>
              <td className="px-2 py-2 text-right tabular-nums text-zinc-500">
                {gap === null ? "" : formatSignedSeconds(gap)}
              </td>
              <td className="w-16 px-3 py-2 text-right">
                {sameGrid ? (
                  <button
                    type="button"
                    disabled={full}
                    onClick={() =>
                      onAdd({ sessionId: entry.session_id, lap: entry.lap_number })
                    }
                    className="rounded-md border border-zinc-700 px-2 py-0.5 text-xs text-zinc-300 transition-colors hover:border-sky-500 hover:text-sky-300 disabled:opacity-40"
                  >
                    {t("compare.add.add")}
                  </button>
                ) : (
                  <span
                    className="text-[10px] uppercase tracking-wider text-zinc-600"
                    title={t("compare.add.otherGridTitle")}
                  >
                    {t("compare.add.otherGrid")}
                  </span>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/** The quickest clean lap of a session: what "this session's best" means. */
function bestLap(laps: SameTrackLap[]): SameTrackLap | null {
  let best: SameTrackLap | null = null;
  for (const lap of laps) {
    if (lap.lap_time_ms === null || lap.is_valid === false || lap.is_out_lap) continue;
    if (best === null || lap.lap_time_ms < (best.lap_time_ms as number)) best = lap;
  }
  return best;
}

/**
 * Why the overlay could not be built, in the reader's language.
 *
 * The API's own message is only worth showing for a bad request, where it names the
 * two tracks or explains the grids and there is nothing here that could say it
 * better. Every other status gets a sentence written for this page: a bare "not
 * found", in English, in red, under a French heading tells a driver nothing about
 * which lap went missing or what to do next.
 */
function failureText(t: TFunction, error: unknown): string {
  if (error instanceof ApiError) {
    switch (error.status) {
      case 400:
        return error.message;
      case 401:
        return t("compare.signIn");
      case 404:
        return t("compare.gone");
      case 409:
        return t("compare.notReady");
    }
  }
  return t("compare.error");
}

/** Elapsed time at the last bucket the lap reached, in seconds. */
function finalTime(lap: AlignedLap): number | null {
  for (let index = lap.time.length - 1; index >= 0; index -= 1) {
    const value = lap.time[index];
    if (value !== null) return value;
  }
  return null;
}
